// Day-of-week x hour-of-day heatmap of message arrivals. Self-contained (no
// chart dependency), matching LineChart.jsx's convention. Sequential blue
// (light -> dark = fewer -> more), computed as an alpha blend over the
// current theme surface so it adapts to light/dark mode automatically.

import { useState } from 'react'
import { Box, Paper, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOUR_LABELS = { 0: '12a', 3: '3a', 6: '6a', 9: '9a', 12: '12p', 15: '3p', 18: '6p', 21: '9p' }
const MIN_ALPHA = 0.06 // zero-count cells still read as part of the grid, not blank
const MAX_ALPHA = 0.88

// grid: 7 arrays (Sun..Sat) of 24 hourly counts. maxCount: the largest single
// cell, for intensity scaling. timezone: shown as a caption (this is bucketed
// in business-hours time, not UTC -- see the repository function's comment).
export default function ArrivalHeatmap({ grid, maxCount, timezone, height = 220 }) {
  const theme = useTheme()
  const [hover, setHover] = useState(null) // { day, hour, count }
  const baseColor = theme.palette.mode === 'dark' ? '#3987e5' : '#2a78d6'
  const rowHeight = Math.max(16, Math.floor((height - 24) / 7))

  if (!grid?.length) {
    return <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>No data for this range.</Typography>
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '36px repeat(24, 1fr)', gap: '2px', alignItems: 'center' }}>
        <Box />
        {Array.from({ length: 24 }, (_, hour) => (
          <Typography key={hour} variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: 10, lineHeight: 1 }}>
            {HOUR_LABELS[hour] ?? ''}
          </Typography>
        ))}

        {DAY_LABELS.map((label, day) => (
          <Box key={label} sx={{ display: 'contents' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{label}</Typography>
            {Array.from({ length: 24 }, (_, hour) => {
              const count = grid[day]?.[hour] ?? 0
              const intensity = maxCount > 0 ? count / maxCount : 0
              return (
                <Box
                  key={hour}
                  onMouseEnter={() => setHover({ day, hour, count })}
                  onMouseLeave={() => setHover((h) => (h?.day === day && h?.hour === hour ? null : h))}
                  sx={{
                    height: rowHeight,
                    borderRadius: '2px',
                    bgcolor: alpha(baseColor, MIN_ALPHA + (MAX_ALPHA - MIN_ALPHA) * intensity),
                  }}
                />
              )
            })}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Fewer</Typography>
        <Box sx={{ width: 120, height: 8, borderRadius: '4px', background: `linear-gradient(to right, ${alpha(baseColor, MIN_ALPHA)}, ${alpha(baseColor, MAX_ALPHA)})` }} />
        <Typography variant="caption" color="text.secondary">More</Typography>
        {timezone && <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>({timezone})</Typography>}
      </Box>

      {hover && (
        <Paper elevation={3} sx={{ position: 'absolute', top: 8, right: 8, p: 1, pointerEvents: 'none' }}>
          <Typography variant="caption" fontWeight={700} display="block">
            {DAY_LABELS[hover.day]} {String(hover.hour).padStart(2, '0')}:00–{String((hover.hour + 1) % 24).padStart(2, '0')}:00
          </Typography>
          <Typography variant="caption">{hover.count} message{hover.count === 1 ? '' : 's'}</Typography>
        </Paper>
      )}
    </Box>
  )
}
